import React from "react";
import "./PublicationOverview.css";

/**
 * How published dashboards and reports are spread across the applications.
 *
 * A labelled bar list rather than a chart: there are at most five rows and each
 * needs its own number and percentage beside the label, which a chart library
 * would make harder rather than easier. Rows and colours come from
 * publicationData in homeApi.js.
 *
 * The bar is drawn from `percent` — the share of *published boards*, not of the
 * counts shown. Those two differ once a board belongs to more than one
 * application, and the percentage is the more useful of the pair.
 */
export default function PublicationOverview({ rows = [], loading = false, error = null }) {
  /* Loading, failed and empty read the same way and are mutually exclusive with
     the bars, so the card keeps its height instead of collapsing between
     states. Loading wins over an error so a refetch does not keep showing the
     last failure. */
  const placeholder = loading
    ? "Loading…"
    : error
      ? "Could not load publication data."
      : rows.length === 0
        ? "Nothing published yet"
        : null;

  return (
    <div className="publication-overview">
      <div className="publication-overview-header">
        <h3>Publication Overview</h3>
      </div>

      {placeholder !== null ? (
        <div className="publication-empty">{placeholder}</div>
      ) : (
        <ul className="publication-list">
          {rows?.map((row) => (
            <li key={row.appId} className="publication-row">
              <div className="publication-row-head">
                <span className="publication-name" title={row.name}>
                  {row.name}
                </span>
                <span className="publication-value">
                  {row.count} <span className="publication-percent">({row.percent}%)</span>
                </span>
              </div>
              {/* The track is always full width; only the fill is scaled, so
                  rows stay aligned regardless of value. A row with a real count
                  keeps a sliver of bar at 0% so it does not read as absent. */}
              <div className="publication-track">
                <div
                  className="publication-fill"
                  style={{ width: `${Math.max(row.percent, row.count > 0 ? 2 : 0)}%`, backgroundColor: row.color }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
